import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { 
  getUserCommissionTransactions, 
  getUserCommissionSummary, 
  processReferralCommission,
  CommissionTransaction,
  CommissionSummary
} from '../utils/referralCommission';

export const useReferralCommission = () => {
  const { currentUser } = useAuth();
  const [transactions, setTransactions] = useState<CommissionTransaction[]>([]);
  const [summary, setSummary] = useState<CommissionSummary>({
    totalEarnings: 0,
    pendingCommissions: 0,
    approvedCommissions: 0,
    totalReferrals: 0
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load user's commission transactions
  const loadCommissionTransactions = useCallback(async () => {
    if (!currentUser) return;

    setLoading(true);
    setError(null);

    try {
      const userTransactions = await getUserCommissionTransactions(currentUser.uid);
      setTransactions(userTransactions);
    } catch (err) {
      setError('Failed to load commission transactions');
      console.error('Error loading commission transactions:', err);
    } finally {
      setLoading(false);
    }
  }, [currentUser]);

  // Load user's commission summary
  const loadCommissionSummary = useCallback(async () => {
    if (!currentUser) return;

    try {
      const userSummary = await getUserCommissionSummary(currentUser.uid);
      setSummary(userSummary);
    } catch (err) {
      console.error('Error loading commission summary:', err);
    }
  }, [currentUser]);

  // Process a new referral commission
  const processCommission = useCallback(async (
    referredBy: string,
    orderId: string,
    paymentId: string,
    items: any[],
    totalAmount: number
  ) => {
    try {
      const commissionId = await processReferralCommission(
        referredBy,
        orderId,
        paymentId,
        items,
        totalAmount
      );
      
      // Reload data after processing
      await loadCommissionTransactions();
      await loadCommissionSummary();
      
      return commissionId;
    } catch (err) {
      setError('Failed to process commission');
      console.error('Error processing commission:', err);
      throw err;
    }
  }, [loadCommissionTransactions, loadCommissionSummary]);

  // Load data when user changes
  useEffect(() => {
    if (currentUser) {
      loadCommissionTransactions();
      loadCommissionSummary();
    } else {
      setTransactions([]);
      setSummary({
        totalEarnings: 0,
        pendingCommissions: 0,
        approvedCommissions: 0,
        totalReferrals: 0
      });
    }
  }, [currentUser, loadCommissionTransactions, loadCommissionSummary]);

  return {
    transactions,
    summary,
    loading,
    error,
    loadCommissionTransactions,
    loadCommissionSummary,
    processCommission,
    hasReferrals: currentUser?.referredBy !== null,
    referredBy: currentUser?.referredBy
  };
};
